import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  PdsBox,
  PdsMantineText,
  PdsTab,
  PdsNetworkIcon,
  PdsHistoryIcon,
} from '@podium-design-system/react-components';
import ApiLimitsChart from '../components/ApiLimitsChart';
import ApiLimitsOverallChart from '../components/ApiLimitsOverallChart';

function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function ApiLimits() {
  const [startDate, setStartDate] = useState(() => {
    const n = new Date();
    return fmt(new Date(n.getFullYear(), n.getMonth(), 1));
  });
  const [endDate, setEndDate] = useState(() => fmt(new Date()));
  const [viewMode, setViewMode] = useState('monthly');
  const [tab, setTab] = useState('limits');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get('/api/api-limits/stats', {
          params: { startDate, endDate },
        });
        if (!cancelled) {
          setData(res.data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'Failed to load API limits');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const warnings = useMemo(() => {
    if (!data?.apis) return [];
    const out = [];
    data.apis.forEach((a) => {
      if (!a.dailyLimit) return;
      a.dailyUsage.forEach((pt) => {
        if (pt.dailyPercentage != null && pt.dailyPercentage >= 80) {
          out.push({ provider: a.name, date: pt.date, pct: pt.dailyPercentage });
        }
      });
    });
    return out;
  }, [data]);

  const overview = data?.overview ?? [];

  return (
    <PdsBox direction="column" gap="lg" className="api-limits-page">
      <PdsBox direction="row" justifyContent="space-between" alignItems="center">
        <PdsMantineText type="display" fontWeight="strong" component="h1" className="argus-type-page-heading">
          API Limits
        </PdsMantineText>
      </PdsBox>

      <div className="api-limits-toolbar">
        <label className="api-limits-field">
          <span>From</span>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label className="api-limits-field">
          <span>To</span>
          <input type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
        {tab === 'tracker' && (
          <div className="api-limits-toggle-group">
            <span className="api-limits-toggle-label">Chart view</span>
            <button
              type="button"
              className={viewMode === 'monthly' ? 'active' : ''}
              onClick={() => setViewMode('monthly')}
            >
              Monthly %
            </button>
            <button
              type="button"
              className={viewMode === 'dailyLimit' ? 'active' : ''}
              onClick={() => setViewMode('dailyLimit')}
            >
              Daily caps
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading">
          <i className="fas fa-spinner fa-spin" /> Loading usage…
        </div>
      )}
      {error && (
        <div className="error">
          <i className="fas fa-exclamation-triangle" /> {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {warnings.length > 0 && (
            <div className="api-limits-warn-banner">
              <i className="fas fa-exclamation-circle" /> Daily usage at or above 80% of the daily cap on:{' '}
              {warnings
                .slice(0, 8)
                .map((w) => `${w.provider} (${w.date}, ${w.pct.toFixed(1)}%)`)
                .join(' · ')}
              {warnings.length > 8 ? ' …' : ''}
            </div>
          )}

          <PdsBox direction="column" gap="md" style={{ marginTop: 8 }}>
            <PdsBox direction="row" gap="xs" wrap="wrap" style={{ borderBottom: '2px solid #e1e4e8' }}>
              <PdsTab
                value="limits"
                selected={tab === 'limits'}
                color="action"
                onClick={() => setTab('limits')}
                leadingIcon={<PdsNetworkIcon />}
              >
                Limits
              </PdsTab>
              <PdsTab
                value="tracker"
                selected={tab === 'tracker'}
                color="action"
                onClick={() => setTab('tracker')}
                leadingIcon={<PdsHistoryIcon />}
              >
                Usage Tracker
              </PdsTab>
            </PdsBox>

            <PdsBox direction="column" gap="md" style={{ paddingTop: 8 }}>
              {tab === 'limits' && (
                <>
                  <PdsMantineText
                    type="headline"
                    fontWeight="strong"
                    color="neutral"
                    component="h2"
                    style={{ margin: 0 }}
                    className="argus-type-section-heading"
                  >
                    Overall Stats
                  </PdsMantineText>
                  <PdsMantineText type="body" color="neutral" style={{ margin: 0, maxWidth: 720 }} className="argus-type-row-meta">
                    Each bar is 100% of that tool&apos;s cap: PublicWWW uses the daily limit (100 calls); SecurityTrails
                    uses the monthly limit (20,000 calls). Figures are as of the &quot;To&quot; date ({endDate}).
                  </PdsMantineText>
                  <ApiLimitsOverallChart overview={overview} />
                </>
              )}

              {tab === 'tracker' && <ApiLimitsChart apis={data.apis} viewMode={viewMode} />}
            </PdsBox>
          </PdsBox>
        </>
      )}
    </PdsBox>
  );
}
